import { CurrencySettings, DateUtil, ModelRow, StringUtil } from "@mcleod/core";
import { IncludeExcludeList } from "@mcleod/dispatch/src/planning/PlanningProfileFilter";
import { PayablesLookup } from "./OrderPayables";
import { OrderPayablesUtil } from "./OrderPayablesUtil";

export class OrderPayablesFilter {
    private filterRow: ModelRow;
    public payablesLookupMap: PayablesLookup;
    private includeLists: IncludeExcludeList[] = [];
    private startDate: Date;
    private cutOffDate: Date;
    private dataPassesCache = new Map<string, boolean>(); //This was added to improve performance when checking currency

    constructor(filterRow: ModelRow) {
        this.filterRow = filterRow;
        this.addIncludeExcludeList("payee_ids", "payee_id");
        this.addIncludeExcludeList("bo_reason_code", "back_office_reason_code");
        this.addIncludeExcludeList("departments", "department");
        this.addIncludeExcludeList("payment_method", "payment_method");
        if (!filterRow.isNull("start_date"))
            this.startDate = DateUtil.parseDateWithKeywords(filterRow.get("start_date"), true, false);
        if (!filterRow.isNull("cutoff_date")) {
            this.cutOffDate = DateUtil.parseDateWithKeywords(filterRow.get("cutoff_date"), true, false);
        }
    }

    public dataPasses(data: ModelRow): boolean {
        let passes = !(data.get("ready to pay flag") === "V" || data.get("record_type") === "O") &&
            (data.get("payee_type") === "R" || data.get("payee_type") === "A");

        if (passes) {
            for (const list of this.includeLists) {
                const listMatches = this.findItem(list.values, data.get(list.dataField));
                if ((list.isExclude && listMatches) || (!list.isExclude && !listMatches)) {
                    passes = false;
                    break;
                }
            }
        }

        passes = passes && this.dateTimeMatches(data)
            && this.payeeStatusMatches(data)
            && this.payeeTypeMatches(data)
            && this.frozenPayablesMatches(data)
            && this.paymentRecipientMatches(data)
            && this.transactionNumMatches(data)
            && this.invoiceReceivedDateMatches(data)
            && this.statusMatches(data);

        this.dataPassesCache.set(data.get("record_id"), passes);
        return passes;
    }

    protected findItem(itemList: string[], item: string): boolean {
        if (itemList == null || itemList.length === 0 || "*" === itemList[0])
            return true;
        if (StringUtil.isEmptyString(item))
            return false;
        return itemList.indexOf(item) >= 0;
    }

    private addIncludeExcludeList(includeExcludeListFieldName, dataFieldName?: string, includeExcludeFlagFieldName?: string) {
        this.includeLists.push({
            isExclude: this.filterRow.getBoolean(includeExcludeFlagFieldName || includeExcludeListFieldName + "_exclude", false),
            values: this.tokenizeField(includeExcludeListFieldName, ","),
            dataField: dataFieldName || includeExcludeListFieldName
        });
    }

    private tokenizeField(field, delimiter: string = ","): string[] {
        if (this.filterRow.isNull(field))
            return [];
        const result = this.filterRow.get(field).split(delimiter);
        for (let i = 0; i < result.length; i++)
            result[i] = result[i].trim();
        return result;
    }

    private dateTimeMatches(data: ModelRow): boolean {
        let filterDate: Date = data.getDate("ok2pay_date");
        if (filterDate == null)
            return false;
        if (typeof filterDate === "string") // bummer - was hoping this would have already been a date in the ModelRow
            filterDate = DateUtil.parseDate(filterDate);
        filterDate = DateUtil.justDate(filterDate);
        return ((this.startDate == null || this.startDate <= filterDate)
            && (this.cutOffDate == null || this.cutOffDate >= filterDate));
    }

    private statusMatches(data: ModelRow): boolean {
        return data.get("ready_to_pay_flag") !== "V";
    }

    private payeeStatusMatches(data: ModelRow): boolean {
        const payeeStatus = this.filterRow.get("payee_status");
        if (payeeStatus == null) {
            return true;
        }
        else {
            return data.get("payee_status") === payeeStatus;
        }
    }

    private payeeTypeMatches(data: ModelRow): boolean {
        const payeeType = this.filterRow.get("payee_type");
        if (payeeType == null) {
            return true;
        }
        else {
            return data.get("payee_type") === payeeType;
        }
    }

    private frozenPayablesMatches(data: ModelRow): boolean {
        const frozenPayables = this.filterRow.get("frozen_payables");
        if (frozenPayables == null) {
            return true;
        } else if (frozenPayables === "S" && !data.isNull("freeze_by")) {
            return true;
        } else if (frozenPayables === "E" && data.isNull("freeze_by")) {
            return true;
        }

        return false;
    }

    private paymentRecipientMatches(data: ModelRow): boolean {
        const paymentRecipient = this.filterRow.get("payment_recipient");
        if (paymentRecipient == null) {
            return true;
        }
        else if (paymentRecipient == "F" && data.getBoolean("pay_factoring_company", false)) {
            return true;
        }
        else if (paymentRecipient == "C" && !data.getBoolean("pay_factoring_company", false)) {
            return true;
        }

        return false;
    }

    private transactionNumMatches(data: ModelRow): boolean {
        const transactionNum = this.filterRow.get("transaction_num");
        if (transactionNum == null) {
            return true;
        }
        return data.get("triumphpay_transaction_num") === transactionNum;
    }

    private invoiceReceivedDateMatches(data: ModelRow): boolean {
        const hasInvoiceReceivedDate = this.filterRow.getBoolean("has_inv_recd_date", false);
        if (!hasInvoiceReceivedDate) {
            return true;
        }
        if (data.get("record_type") == "P") {
            if (data.isNull("order_id") || data.get("payee_type") != "R") {
                return true;
            }
            const key = PayablesLookup.key(data);
            const modelRow = this.payablesLookupMap.getSettlementWithKey(key);
            if (OrderPayablesUtil.matchingPayable(modelRow, data) && modelRow.get("record_type") == "S"
                && !modelRow.isNull("invoice_recv_date")) {
                return true;
            }
            return data.getBoolean("has_settle_hist", false);
        }
        return !data.isNull("invoice_recv_date");
    }

    public currencyMatches(data: ModelRow): boolean {
        const currencySettings = CurrencySettings.getSingleton();
        const multicurrencyEnabled = currencySettings.multiCurrencyEnabled();
        if (!multicurrencyEnabled) return true;

        const currency_code = this.filterRow.get("currency_type");
        if (currency_code == null) return true;

        if (currency_code == data.getCurrency("pay_amount")?.getCurrencyCode()) return true;

        return false;
    }

    public getFilterCurrencyType(){
        return this.filterRow.get("currency_type");
    }
}
